import ElementNames from './elementNames';
import generateContentText from './generateContentText';
import { ITemplateHistory, ITemplateHistoryImage, ITemplateHistoryText } from '../../models';
import { VisualDesign, VisualElement } from '@wm-mfes/w5g';
import { IVisualDesignData } from '@communication-center/data-objects';

function findElementTextByName(root: VisualElement, name: string): ITemplateHistoryText | undefined {
    if (root.Type === 'Text' || (root.Type === 'Rich-Text' && root.Name === name && root.Content)) {
        return { text: generateContentText(root.Content), element: root };
    }

    if (root.ChildElements) {
        const element = root.ChildElements.map((child: $TSFixMe) => findElementTextByName(child, name)).find(Boolean);

        if (element) {
            return element;
        }
    }

    return undefined;
}

function findMainImageSrc(root: $TSFixMe): ITemplateHistoryImage | null {
    if (root.Type === 'Image' && root.Name === 'Image') {
        return {
            src: root.Content?.ImageSource,
            aspectRatio: root?.Style?.size?.aspectRatioLock ? root?.Style.size.aspectRatio : null
        };
    }

    if (root.ChildElements) {
        const image: ITemplateHistoryImage = root.ChildElements.map((child: $TSFixMe) => findMainImageSrc(child)).find(Boolean);

        if (image) {
            return image;
        }
    }

    return null;
}

export function findActionElements(root: VisualElement, actions: VisualElement[] = []) {
    if (root?.Action?.type) {
        actions.push(root);
    }

    if (root.ChildElements) {
        root.ChildElements.forEach(child => findActionElements(child, actions));
    }

    return actions;
}

function _extractButtons({
    visualDesignData,
    type = 'Button'
}: {
    visualDesignData?: VisualDesign;
    type?: 'Button' | 'XButton';
}): { label: string; id: string }[] {
    if (!visualDesignData) {
        return [];
    }

    const elements = findActionElements(visualDesignData.RootVisualElement);

    const buttons: Array<{ id: string; label: string }> = [];

    elements.forEach(element => {
        if (element.Type === type) {
            buttons.push({
                label: element.Content?.Text,
                id: element.Id
            });
        }

        const btn = element?.ChildElements?.find(child => child.Type === type);

        if (btn) {
            buttons.push({
                label: btn.Content?.Text,
                id: element.Id
            });
        }
    });

    return buttons;
}

export function extractButtons(visualDesignData: IVisualDesignData): { label: string; id: string }[] {
    return _extractButtons({ visualDesignData: visualDesignData.VisualDesign });
}

export function extractXButtons(visualDesignData: IVisualDesignData): { label: string; id: string }[] {
    return _extractButtons({ visualDesignData: visualDesignData.VisualDesign, type: 'XButton' });
}

export function findDependencies(root: VisualElement, deps:  { dependencyType: number; dependencyId: number }[] = []) {
  const typesMap = {'playShuttle' : 9, 'playBf': 19, 'playResource': 7}
  const types = Object.keys(typesMap)
  if (root.Type === 'Rich-Text') {
    root.Content.Quill.ops.forEach(({attributes }: {attributes: Record<string, any>})=>{
      if (attributes?.link) {
        const link = JSON.parse(attributes.link)
        if (typeof link === 'object' && 'type' in link && 'itemId' in link && types.includes(link.type)) {
          deps.push({dependencyType: typesMap[link.type as keyof typeof typesMap], dependencyId: link.itemId})
        }
      }
    })
  } else if (root?.Action?.type && types.includes(root.Action.type)) {
    deps.push({dependencyType:  typesMap[root.Action.type as keyof typeof typesMap], dependencyId: root.Action.itemId!});
  }
  if (root.ChildElements) {
    root.ChildElements.forEach(child => findDependencies(child, deps));
  }
  return deps
}

export default function extractVisualDesignDataProps(template: $TSFixMe): ITemplateHistory {
    const title = findElementTextByName(template?.VisualDesign?.RootVisualElement, ElementNames.TITLE);
    const message = findElementTextByName(template?.VisualDesign?.RootVisualElement, ElementNames.DESCRIPTION);
    const image = findMainImageSrc(template?.VisualDesign?.RootVisualElement);
    const dependencies = findDependencies(template?.VisualDesign?.RootVisualElement);

    return {
        title,
        message,
        image: image ?? null,
        dependencies
    };
}
